import { Helmet } from "react-helmet-async";
import { useEffect, useRef } from "react";
import PageHero from "../../../components/PageHero";
import AppStoreBtns from "../../../components/AppStoreBtns";
import cryptoBro from "../../../assets/images/Cryptobro.png";
import deviceAddons from "../../../assets/images/device-addons.png";
import securedTrade from "../../../assets/images/secured-trade.png";
import cashout from "../../../assets/images/cashout.png";
import "../products.css";

const CryptoHero = () => {
  return (
    <section className="flex flex-col xs:gap-3 gap-10 items-center px-5 mt-5">
      <div className="text-center flex flex-col items-center gap-5 xs:gap-10 mt-10 px-5 xs:px-0">
        <h2 className="font-mabry-bold xs:text-6xl md:text-7xl text-4xl">
          Convert Crypto to Naira Instantly
        </h2>
        <p className="md:text-3xl xs:text-2xl text-base md:leading-10 max-w-[800px]">
          Trade your Bitcoin, USDT, and other cryptocurrencies at the best rates
          with instant payment to your Asapuu wallet
        </p>
        <AppStoreBtns />
      </div>
      <div className="xs:max-w-[70%] max-w-[80%] lg:h-[500px] xs:h-[300px] h-[250px] overflow-hidden relative">
        <img
          src={deviceAddons}
          alt="Asapuu app interface"
          className="relative slide-up"
          loading="lazy"
        />
        <img
          src={cryptoBro}
          alt="Crypto trader"
          className="absolute bottom-0 right-0 w-[150px] sm:w-[200px]"
        />
      </div>
    </section>
  );
};

const Features = () => {
  const features = [
    {
      title: "Naira Wallet",
      description:
        "Store your funds securely in your Naira wallet, perfect for instant bill payments and airtime purchases.",
      image: cashout,
    },
    {
      title: "Crypto Wallet",
      description:
        "Get your unique wallet address for easy deposits of Bitcoin, USDT, and other supported cryptocurrencies.",
      image: cryptoBro,
    },
    {
      title: "P2P Transfers",
      description:
        "Send crypto to other Asapuu users instantly using their Asapuu tags - fast and fee-free.",
      image: securedTrade,
    },
  ];

  return (
    <section className="container mx-auto px-5 my-20">
      <h3 className="text-center font-mabry-bold text-4xl sm:text-5xl mb-16">
        Features You'll Love
      </h3>
      <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8 max-w-[1200px] mx-auto">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white rounded-[30px] p-8 shadow-md text-center group hover:scale-105 transition-transform duration-300"
          >
            <div className="mb-6 flex justify-center">
              <img
                src={feature.image}
                alt={feature.title}
                className="w-32 h-32 object-contain"
              />
            </div>
            <h4 className="font-mabry-bold text-2xl sm:text-3xl text-primary mb-4">
              {feature.title}
            </h4>
            <p className="text-lg sm:text-xl font-mabry-light">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-section');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: "01",
      title: "Download & Sign Up",
      description: "Get the Asapuu app and create your account in minutes.",
    },
    {
      number: "02",
      title: "Get Your Wallet",
      description:
        "Your crypto wallet address is automatically generated upon verification.",
    },
    {
      number: "03",
      title: "Send Crypto",
      description: "Deposit crypto to your Asapuu wallet address.",
    },
    {
      number: "04",
      title: "Convert to Naira",
      description:
        "Sell your crypto instantly at the best rates to your Naira wallet.",
    },
    {
      number: "05",
      title: "Use Your Funds",
      description: "Pay bills, buy airtime, or transfer to your bank account.",
    },
  ];

  return (
    <section className="bg-primary text-white py-20 overflow-hidden">
      <div className="container mx-auto px-5">
        <h3 className="text-center font-mabry-bold text-4xl sm:text-5xl mb-16 animate-fadeIn">
          How It Works
        </h3>
        <div ref={sectionRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-[1200px] mx-auto">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative step-animation-${index} opacity-0`}
            >
              <span className="text-6xl sm:text-7xl font-mabry-bold opacity-20 group-hover:opacity-40 transition-opacity">
                {step.number}
              </span>
              <div className="mt-4 hover:translate-x-2 transition-transform">
                <h4 className="font-mabry-bold text-2xl mb-2">{step.title}</h4>
                <p className="font-mabry-light text-lg">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Crypto = () => {
  return (
    <main className="mb-80">
      <Helmet prioritizeSeoTags>
        <title>Crypto Trading - Convert Cryptocurrency to Naira | Asapuu</title>
        <meta
          name="description"
          content="Convert your cryptocurrency to Naira instantly with Asapuu. Get the best rates for Bitcoin, USDT, and other digital assets with secure and instant settlements."
        />
        <meta
          property="og:title"
          content="Crypto Trading - Convert Cryptocurrency to Naira | Asapuu"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.asapuu.com/products/crypto"
        />
        <meta
          property="og:description"
          content="Convert your cryptocurrency to Naira instantly with Asapuu. Get the best rates for Bitcoin, USDT, and other digital assets with secure and instant settlements."
        />
        <link rel="canonical" href="https://www.asapuu.com/products/crypto" />
      </Helmet>
      <PageHero
        title="Crypto Trading"
        info="Convert your cryptocurrency to Naira securely and instantly"
      />
      <CryptoHero />
      <Features />
      <HowItWorks />
    </main>
  );
};

export default Crypto;
